import React, { useState } from "react";
import { s } from "../lib/theme";
import { Users, errMsg } from "../lib/api";

export default function TeamAdmin({ users, requests, refreshAll }) {
  const [showInactive, setShowInactive] = useState(false);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const active = users.filter((u) => u.role !== "admin" && u.active !== false)
    .sort((a, b) => a.name.localeCompare(b.name));
  const employees = showInactive
    ? [...active, ...inactiveUsers.filter((u) => u.role !== "admin")].sort((a, b) => a.name.localeCompare(b.name))
    : active;
  const [err, setErr] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "", tier_hours: 80, hire_date: "", balance_hours: 0 });

  async function toggleShowInactive(next) {
    setShowInactive(next);
    if (next) {
      try {
        const all = await Users.list(true);
        setInactiveUsers(all.filter((u) => u.active === false));
      } catch (e) {
        setErr(errMsg(e, "Could not load archived employees."));
      }
    }
  }

  function pendingPto(id) {
    return requests
      .filter((r) => r.user_id === id && r.status === "pending" && r.hours > 0)
      .reduce((sum, r) => sum + r.hours, 0);
  }

  async function update(id, field, value) {
    setErr("");
    try {
      await Users.update(id, { [field]: value });
      await refreshAll();
      if (showInactive) await toggleShowInactive(true);
    } catch (e) {
      setErr(errMsg(e, "Update failed."));
    }
  }

  async function resetPw(u) {
    if (!window.confirm(`Reset ${u.name}'s password to the starter? They'll be required to set a new private password at next sign-in.`)) return;
    try {
      await Users.resetPassword(u.id);
      await refreshAll();
    } catch (e) {
      setErr(errMsg(e, "Reset failed."));
    }
  }

  async function archive(u) {
    if (!window.confirm(
      `Archive ${u.name}?\n\n` +
      `They will be hidden from the active team list, cannot sign in, and won't appear in the "Log Time Off" dropdown or Coverage legend.\n\n` +
      `All their historical requests, attachments, and payroll records are kept. ` +
      `You can restore them later with "Show former employees".`
    )) return;
    try {
      await Users.remove(u.id);
      await refreshAll();
      if (showInactive) await toggleShowInactive(true);
    } catch (e) {
      setErr(errMsg(e, "Could not archive."));
    }
  }

  async function restore(u) {
    if (!window.confirm(`Restore ${u.name} to the active team? They'll be able to sign in again.`)) return;
    try {
      await Users.restore(u.id);
      await refreshAll();
      await toggleShowInactive(true);
    } catch (e) {
      setErr(errMsg(e, "Could not restore."));
    }
  }

  async function addUser() {
    setErr("");
    if (!draft.name.trim() || !draft.email.trim()) {
      setErr("Name and email are required.");
      return;
    }
    try {
      await Users.create({
        name: draft.name.trim(),
        email: draft.email.trim().toLowerCase(),
        role: "employee",
        tier_hours: Number(draft.tier_hours || 0),
        hire_date: draft.hire_date || "",
        balance_hours: Number(draft.balance_hours || 0),
      });
      setDraft({ name: "", email: "", tier_hours: 80, hire_date: "", balance_hours: 0 });
      setAddOpen(false);
      await refreshAll();
    } catch (e) {
      setErr(errMsg(e, "Could not add team member."));
    }
  }

  return (
    <div style={s.card} data-testid="team-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
        <div style={s.cardTitle}>Team &amp; PTO Balances</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input
              data-testid="team-show-inactive"
              type="checkbox"
              checked={showInactive}
              onChange={(e) => toggleShowInactive(e.target.checked)}
            />
            Show former employees
          </label>
          <button data-testid="team-add-btn" style={s.btnGhost} onClick={() => setAddOpen((v) => !v)}>
            {addOpen ? "Cancel" : "+ Add team member"}
          </button>
        </div>
      </div>
      <div style={{ ...s.muted, marginBottom: 14 }}>
        Set each person&apos;s email, hire date, annual tier, and starting balance. Approving a PTO
        request automatically subtracts hours here. Tiers from the manual: 1–3 yrs = 80 · 3+ yrs
        = 120 · 6+ yrs = 160. <strong>Archiving</strong> a team member hides them from active views
        but preserves all their historical records.
      </div>

      {addOpen && (
        <div style={{ ...s.card, background: "#f8fafc" }} data-testid="team-add-form">
          <div style={s.cardTitle}>Add a team member</div>
          <div style={s.row2}>
            <div>
              <label style={s.label}>Name</label>
              <input data-testid="team-add-name" style={s.input} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <label style={s.label}>Work email</label>
              <input data-testid="team-add-email" style={s.input} type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
            </div>
            <div>
              <label style={s.label}>Hire date</label>
              <input data-testid="team-add-hire" style={s.input} type="date" value={draft.hire_date} onChange={(e) => setDraft({ ...draft, hire_date: e.target.value })} />
            </div>
            <div>
              <label style={s.label}>Annual tier (hrs)</label>
              <select data-testid="team-add-tier" style={s.input} value={draft.tier_hours} onChange={(e) => setDraft({ ...draft, tier_hours: Number(e.target.value) })}>
                <option value={0}>0</option>
                <option value={24}>prorated (24)</option>
                <option value={80}>80</option>
                <option value={120}>120</option>
                <option value={160}>160</option>
              </select>
            </div>
            <div>
              <label style={s.label}>Starting balance (hrs)</label>
              <input data-testid="team-add-balance" style={s.input} type="number" value={draft.balance_hours} onChange={(e) => setDraft({ ...draft, balance_hours: e.target.value })} />
            </div>
          </div>
          <button data-testid="team-add-save" style={s.btnPrimary} onClick={addUser}>Save team member</button>
        </div>
      )}

      {err && <div style={s.error} data-testid="team-err">{err}</div>}

      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Name</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>Hire date</th>
              <th style={s.th}>Annual tier (hrs)</th>
              <th style={s.th}>Balance (hrs)</th>
              <th style={s.th}>Pending PTO</th>
              <th style={s.th}>Password</th>
              <th style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((u) => (
              <tr key={u.id} data-testid={`team-row-${u.id}`} style={u.active === false ? { opacity: 0.55, background: "#fafafa" } : {}}>
                <td style={s.td}>
                  {u.name}
                  {u.active === false && (
                    <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#6b7280", background: "#e5e7eb", padding: "2px 6px", borderRadius: 999, textTransform: "uppercase", letterSpacing: ".4px" }}>
                      Archived
                    </span>
                  )}
                </td>
                <td style={s.td}>
                  <input data-testid={`team-email-${u.id}`} style={s.cellInput} type="email" defaultValue={u.email} onBlur={(e) => e.target.value !== u.email && update(u.id, "email", e.target.value)} disabled={u.active === false} />
                </td>
                <td style={s.td}>
                  <input data-testid={`team-hire-${u.id}`} style={s.cellInput} type="date" value={u.hire_date || ""} onChange={(e) => update(u.id, "hire_date", e.target.value)} disabled={u.active === false} />
                </td>
                <td style={s.td}>
                  <select data-testid={`team-tier-${u.id}`} style={s.cellInput} value={u.tier_hours} onChange={(e) => update(u.id, "tier_hours", Number(e.target.value))} disabled={u.active === false}>
                    <option value={0}>0</option>
                    <option value={24}>prorated (24)</option>
                    <option value={80}>80</option>
                    <option value={120}>120</option>
                    <option value={160}>160</option>
                  </select>
                </td>
                <td style={s.td}>
                  <input data-testid={`team-balance-${u.id}`} style={s.cellInput} type="number" defaultValue={u.balance_hours} onBlur={(e) => Number(e.target.value) !== Number(u.balance_hours) && update(u.id, "balance_hours", Number(e.target.value))} disabled={u.active === false} />
                </td>
                <td style={{ ...s.td, color: pendingPto(u.id) > 0 ? "#92400e" : "#9ca3af" }}>
                  {pendingPto(u.id) > 0 ? `${pendingPto(u.id)} hrs awaiting` : "—"}
                </td>
                <td style={s.td}>
                  {u.must_change_password
                    ? <span style={{ fontSize: 12, color: "#92400e" }}>starter (unset)</span>
                    : <span style={{ fontSize: 12, color: "#166534" }}>set ✓</span>}
                </td>
                <td style={s.td}>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {u.active === false ? (
                      <button data-testid={`team-restore-${u.id}`} style={{ ...s.btnGhost, padding: "5px 10px", fontSize: 12, color: "#0f5132", borderColor: "#bbf7d0" }} onClick={() => restore(u)}>
                        Restore
                      </button>
                    ) : (
                      <>
                        <button data-testid={`team-reset-${u.id}`} style={{ ...s.btnGhost, padding: "5px 10px", fontSize: 12 }} onClick={() => resetPw(u)}>
                          Reset PW
                        </button>
                        <button data-testid={`team-archive-${u.id}`} style={{ ...s.btnGhost, padding: "5px 10px", fontSize: 12, color: "#b91c1c", borderColor: "#fecaca" }} onClick={() => archive(u)}>
                          Archive
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ ...s.muted, marginTop: 14, fontSize: 12, lineHeight: 1.5 }}>
        Tip: at the start of each year your manual grants half the annual tier on Jan 2 and half
        on Jul 1. Add those hours to each balance here when the time comes.
      </div>
    </div>
  );
}
